import React, {ReactNode, useState} from "react";
import {GateKeeperRecordDto} from "../../services/messages/GateKeeperRecordDto";
import {Accordion, AccordionSummary} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import "./AccountsListView.scss";
import classNames from 'classnames';
import {isAccountContent} from '../../domain/ItemContent';
import {AccountImage} from './AccountImage';
import {ContentLocation} from "../../locations/ContentLocation";
import {useAppSelector} from "../../hooks/ReduxHooks";
import ShareIcon from "@mui/icons-material/Share";
import {AccountCategoryImage} from "./AccountCategoryImage";
import {getItemContent} from "../../state/ItemState";

function groupBy<K, V>(list: V[], keyGetter: (value: V) => K) {
    const map = new Map<K, V[]>();
    list.forEach((item) => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        } else {
            collection.push(item);
        }
    });
    return map;
}

export const AccountsListView = () => {

    const isMobile = useAppSelector(({system}) => system.isMobile);

    const [expanded, setExpanded] = useState<string | false>(false);

    const accountSearchText = useAppSelector(s => s.accountsState.accountSearchText).toLowerCase();
    const accounts = useAppSelector(s => s.accountsState.accounts);
    const selectedAccountClientId = useAppSelector(s => {
        const content = getItemContent(s.itemState);
        return isAccountContent(content) ? content.clientId : undefined;
    });

    const handleChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    function onSelectAccount(account: GateKeeperRecordDto) {
        ContentLocation.showAccount(account.Id, account.ClientId);
    }

    function renderAccount(account: GateKeeperRecordDto): ReactNode {
        return (
            <div key={account.Id}
                 className={classNames("account-summary", "selectable", {"selected": selectedAccountClientId === account.ClientId})}
                 onClick={() => onSelectAccount(account)}
            >
                {typeof account.IsOwner !== 'undefined' && !account.IsOwner && <ShareIcon className="share-icon"/>}
                <AccountImage image={account.Image} showDefault={true}/>
                <span className="name">{account.Name}</span>
            </div>
        );
    }

    function isAccountMatch(account: GateKeeperRecordDto) {
        return account.Category.toLowerCase().includes(accountSearchText)
            || account.Name.toLowerCase().includes(accountSearchText);
    }

    function renderCategory(accounts: GateKeeperRecordDto[]): ReactNode | null {
        const category = accounts[0].Category;
        accounts.sort((a, b) => a.Name.localeCompare(b.Name));
        return (
            <Accordion key={category} expanded={expanded === category} onChange={handleChange(category)} TransitionProps={{unmountOnExit: true, timeout: isMobile ? 0 : 100}}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>} className="category selectable-2">
                    <AccountCategoryImage category={category}/>
                    <span>{category}</span>
                </AccordionSummary>

                {accounts.map(account => renderAccount(account))}

            </Accordion>
        );
    }

    const filteredAccounts = (accountSearchText.length === 0
        ? accounts
        : accounts.filter(isAccountMatch))
        .filter(a => a.IsArchived === 0);

    const categories = groupBy(filteredAccounts, a => a.Category);

    if (categories.size === 1 && filteredAccounts.length > 0 && expanded !== filteredAccounts[0].Category) {
        // Auto-expand if there's only one category being displayed
        setExpanded(filteredAccounts[0].Category);
    }

    const sortedCategories: GateKeeperRecordDto[][] = [];
    categories.forEach(accounts => sortedCategories.push(accounts));

    const categoryElts = sortedCategories
        .sort((a, b) => a[0].Category.localeCompare(b[0].Category))
        .map(category => renderCategory(category));

    return (
        <div className="content-item-list accounts-view">
            <div className="accounts-list">
                {categoryElts}
            </div>
        </div>
    )
}
